import { BASE_API_URL, BASE_NEW_API_URL } from '@/constants';
import axios, {
  AxiosError,
  AxiosInstance,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import * as authStorage from './authStorage';
import { ensureFreshAccessToken, forceLogout, refreshTokens } from './tokenAuth';

export const axiosInstance = axios.create({
  baseURL: BASE_API_URL,
});

export const axiosInstanceNewAPI = axios.create({
  baseURL: BASE_NEW_API_URL,
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { isRetriedRequest?: boolean };

const attachErrorContext = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  // eslint-disable-next-line no-param-reassign
  config.errorContext = new Error('Thrown at:');
  return config;
};

const attachAuthHeader = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  if (authStorage.getAccessToken()) {
    // A refresh that failed for a temporary reason keeps the session; the request goes out with
    // the current token and a 401 is handled by handleAuthError like any other.
    await ensureFreshAccessToken().catch(() => undefined);
    const accessToken = authStorage.getAccessToken();
    if (accessToken) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }
  return config;
};

const describeRequest = (error: AxiosError): string => {
  const { config, response, code, message } = error;
  const method = config?.method ? config.method.toUpperCase() : 'UNKNOWN';
  const url = [config?.baseURL, config?.url].filter(Boolean).join('');
  const params = config?.params ? JSON.stringify(config.params) : undefined;

  const lines = [
    `Request failed: ${method} ${url || '(no url)'}`,
    params ? `Params: ${params}` : undefined,
    response ? `Response: ${response.status} ${response.statusText}` : 'Response: (no response)',
    code ? `Code: ${code}` : undefined,
    message ? `Message: ${message}` : undefined,
  ].filter(Boolean);

  return lines.join('\n');
};

const enrichError = async (error: AxiosError): Promise<never> => {
  const requestDetails = describeRequest(error);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const originalStackTrace = error.config?.errorContext?.stack as string | undefined;

  // eslint-disable-next-line no-param-reassign
  error.stack = [error.stack, requestDetails, originalStackTrace].filter(Boolean).join('\n');

  throw error;
};

const hasStoredTokens = (): boolean =>
  Boolean(authStorage.getAccessToken() || authStorage.getRefreshToken());

/**
 * A 401 means "this token is dead" on every endpoint, including the ones that need no
 * authentication at all - so a leftover token would make the whole app unusable if we kept it.
 * The token is refreshed once; when that is impossible or the refresh token is rejected, both
 * tokens are dropped so the app can carry on anonymously. A refresh that fails for a temporary
 * reason keeps the session. A 403 means "authenticated, but not allowed" and never touches
 * the session.
 */
const handleAuthError = (instance: AxiosInstance, error: AxiosError): Promise<unknown> => {
  const config = error.config as RetryableRequestConfig | undefined;

  if (error.response?.status !== HttpStatusCode.Unauthorized) {
    return Promise.reject(error);
  }

  if (config && !config.isRetriedRequest && authStorage.getRefreshToken()) {
    // eslint-disable-next-line no-param-reassign
    config.isRetriedRequest = true;
    return refreshTokens().then(
      refreshed => {
        if (!refreshed) {
          // refreshTokens() already forced a logout (clears storage + notifies the app)
          // when the refresh token was rejected.
          return Promise.reject(error);
        }
        const accessToken = authStorage.getAccessToken();
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return instance(config);
      },
      // The refresh failed for a temporary reason (network, server error) - keep the session
      // and report the original 401.
      () => Promise.reject(error),
    );
  }

  if (hasStoredTokens()) {
    // Either there was nothing left to refresh with, or the freshly refreshed token was
    // rejected as well - the stored tokens are dead either way.
    forceLogout();
  }

  return Promise.reject(error);
};

const registerInterceptors = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(attachErrorContext);
  instance.interceptors.request.use(attachAuthHeader);
  instance.interceptors.response.use(undefined, error => handleAuthError(instance, error));
  instance.interceptors.response.use(undefined, enrichError);
};

registerInterceptors(axiosInstance);
registerInterceptors(axiosInstanceNewAPI);
